/**
 * @fileoverview abstracts the multiple ways of requesting the mainissueaggquery.
 * @todo Will be consolidated in https://jplat.atlassian.net/browse/JIV-19486
 */
/* eslint-disable prefer-object-spread */ // HOT-99984 Object spread in vendorless entries pulls in vendor.js
import type {
	ISSUE_VIEW_TYPE,
	CAPABILITY_HEADER_NAME_TYPE,
} from '@atlassian/jira-capabilities/src/constants.tsx';
import { getMainIssueViewAggQueryVariables } from '@atlassian/jira-issue-fetch-services-common/src/services/issue-agg-data/utils.tsx';
import { IssueViewFetchOperations } from '@atlassian/jira-issue-fetch-services-common/src/types.tsx';
import {
	earlyFetch,
	putEarlyScriptPromise,
	getEarlyScriptPromiseKey,
} from '@atlassian/jira-relay-vendorless-utils/src/services/early-script-promise/index.tsx';
import mainIssueAggQuery from '@atlassian/jira-relay/src/__generated__/mainIssueAggQuery$parameters';
import { getEnhancedCapabilityHeader } from '@atlassian/jira-ufo-capability-header/src/index.tsx';
import { getAggEndpoint, getRequestBody } from '@atlassian/relay-endpoint';
import { fg } from '@atlassian/jira-feature-gating';
import fetchJson from '../../common/utils/fetch-json.tsx';
import scrapeJsonFromInitialPageResponse from '../../common/utils/scrape-json.tsx';

const ISSUE_VIEW: ISSUE_VIEW_TYPE = 'ISSUE_VIEW';
const CAPABILITY_HEADER_NAME: CAPABILITY_HEADER_NAME_TYPE = 'X-Atlassian-Capability';

const getVariables = (cloudId: string, issueKey: string, siteAri: string) => {
	const projectKey = issueKey.split('-')[0];

	return Object.assign(
		{
			cloudId,
			issueKey,
			siteAri,
			projectKey,
		},
		getMainIssueViewAggQueryVariables(issueKey),
	);
};

/**
 * Scrap AGG data from the initial page response written by bifrost template.
 * @returns Promise<Object> of scraped AGG data
 */
export const getAggDataFromInitialPageResponse = () =>
	scrapeJsonFromInitialPageResponse(IssueViewFetchOperations.EARLY_FETCH_AGG);

/**
 * Write the result of the delegate promise to the early script promise map.
 * This is meant to be used in place of `fetchIssueRelayData` if the AGG data
 * is already available on the initial page response.
 *
 * @param delegate - Promise of the function that will resolve the AGG data
 */
export const fetchIssueRelayDataViaDelegate = (
	issueKey: string,
	cloudId: string,
	siteAri: string,
	delegate: Promise<Object>,
) => {
	const { params } = mainIssueAggQuery;
	const variables = getVariables(cloudId, issueKey, siteAri);
	const key = getEarlyScriptPromiseKey(params, variables);

	const promise = delegate.then((data) => {
		// potential future optimizations:
		// * if scraped data can return stringified JSON, we can avoid the JSON.stringify here
		// * or if the relay cache contract accepts Map<String, Promise<JSONObject>> instead of Map<String, Promise<Response>>
		// https://stash.atlassian.com/projects/JIRACLOUD/repos/jira-frontend/pull-requests/103031/overview?commentId=5814184
		const jsonData = JSON.stringify(data);
		return new Response(jsonData);
	});

	putEarlyScriptPromise(key, promise);
};

/**
 * Early fetch for AGG issue data.
 *
 * To access the resulting data, use normal Relay APIs with the same query and variables.
 *
 * No return value because the next network call from the Relay client for a matching queryid+variables
 * combo will use (or wait for) this early request instead of making a new one.
 */
export const fetchIssueRelayData = (issueKey: string, cloudId: string, siteAri: string) =>
	earlyFetch(mainIssueAggQuery, getVariables(cloudId, issueKey, siteAri), getHeaders(), ISSUE_VIEW);

export const getHeaders = () => {
	const headers = ['JiraIssue', 'projectStyle', 'GlobalTimeTrackingSettings'];
	if (fg('smart-replies-system-setting')) {
		headers.push('JiraApplicationProperties');
	}
	if (
		fg('issue-jiv-19838-chin-api-lookup-through-issue-key') ||
		fg('jira-linked-work-items-table-kill-switch')
	) {
		headers.push('JiraIssueSearch');
	}
	return headers;
};

/**
 * @deprecated Prefer {@link fetchIssueRelayData}.The new one allows us to avoid managing the result promise in a issue-view bespoke way for our early resources.
 */
export const fetchIssueAggData = (issueKey: string, cloudId: string, siteAri: string) => {
	const endpoint = getAggEndpoint(mainIssueAggQuery.params);
	const variables = getVariables(cloudId, issueKey, siteAri);
	const body = getRequestBody(mainIssueAggQuery.params, variables);
	const capabilityHeader = getEnhancedCapabilityHeader(ISSUE_VIEW);
	const options = {
		method: 'POST',
		body,
		headers: {
			'content-type': 'application/json',
			'X-ExperimentalApi': getHeaders().toString(),
			...(capabilityHeader ? { [CAPABILITY_HEADER_NAME]: capabilityHeader } : {}),
		},
	};

	return fetchJson(endpoint, options);
};
